import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

interface PaymentStatusProps {
  onSuccess?: (sessionId: string) => void;
  onCancel?: () => void;
}

export const StripePaymentStatus: React.FC<PaymentStatusProps> = ({ onSuccess, onCancel }) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'canceled' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const checkout = searchParams.get('checkout');
      const success = checkout === 'success' || !!searchParams.get('success');
      const canceled = checkout === 'cancel' || !!searchParams.get('canceled');
      const sessionId = searchParams.get('session_id');

      if (canceled) {
        setStatus('canceled');
        setMessage('Pagamento cancelado pelo usuário.');
        onCancel?.();
        return;
      }

      if (sessionId) {
        try {
          // Verificar status da sessão no servidor Stripe
          const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          const serverUrl = isDev
            ? ((window as any).ENV?.VITE_STRIPE_SERVER_URL || import.meta.env.VITE_STRIPE_SERVER_URL || 'http://localhost:3001')
            : '';

          const apiEndpoint = isDev
            ? `${serverUrl}/session-status?session_id=${sessionId}`
            : `/api/session-status?session_id=${sessionId}`;

          const response = await fetch(apiEndpoint);

          if (!response.ok) {
            throw new Error('Erro ao verificar sessão');
          }

          const session = await response.json();

          if (session.payment_status === 'paid') {
            setStatus('success');
            setMessage('Pagamento realizado com sucesso! Seu plano foi ativado.');
            onSuccess?.(sessionId);
            // Disparar eventos para atualizar todos os componentes relacionados
            try {
              window.dispatchEvent(new Event('paymentHistoryRefresh'));
              window.dispatchEvent(new Event('profileRefresh')); // Atualizar profile
              window.dispatchEvent(new Event('userPlanRefresh')); // Atualizar dados do plano
              window.dispatchEvent(new CustomEvent('planActivated', { detail: { sessionId } })); // Notificar ativação do plano
            } catch { }
          } else {
            setStatus('error');
            setMessage('Erro no processamento do pagamento.');
          }
        } catch (error) {
          console.error('Erro ao verificar status:', error);
          setStatus('error');
          setMessage('Erro ao verificar status do pagamento.');
        }
      } else {
        setStatus('error');
        setMessage('Parâmetros inválidos: session_id ausente.');
      }
    };

    checkPaymentStatus();
  }, [searchParams, onSuccess, onCancel]);

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-600" />;
      case 'canceled':
        return '❌';
      case 'error':
        return '⚠️';
      default:
        return '⏳';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'canceled':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  if (status === 'loading') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span>Verificando status do pagamento...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
        <div className={`p-4 rounded-lg border ${getStatusColor()}`}>
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{getStatusIcon()}</span>
            <div>
              <h3 className="font-semibold">
                {status === 'success' && 'Pagamento Aprovado!'}
                {status === 'canceled' && 'Pagamento Cancelado'}
                {status === 'error' && 'Erro no Pagamento'}
              </h3>
              <p className="text-sm mt-1">{message}</p>
            </div>
          </div>
        </div>

        <div className="mt-6 px-2">
          <button
            onClick={() => {
              // Fechar o modal removendo os parâmetros da URL
              const url = new URL(window.location.href);
              url.searchParams.delete('success');
              url.searchParams.delete('canceled');
              url.searchParams.delete('session_id');
              window.history.replaceState({}, '', url.toString());

              // Redirecionar para o dashboard
              window.location.href = '/dashboard';
            }}
            className="w-full py-3 bg-transparent text-green-600 border-2 border-green-600 rounded-lg hover:bg-green-50 hover:border-green-700 hover:text-green-700 transition-all duration-200 font-semibold text-base focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}; 
